<script setup lang="ts">
/**
 * The trace flow, drawn horizontally: depth grows rightwards, siblings stack downwards.
 *
 * Purpose-built rather than the design system's `Tree`, which is vertical and file-tree shaped.
 * Bending it into a status-coloured horizontal graph would cost more than this component, and a
 * graph library would cost far more.
 */
import { computed, ref, watch } from 'vue'
import { useLocalization } from '@hantera/portal-app'
import type { TraceNode } from '@/backend/traces.types'
import { buildTraceGraphModel, findAncestorKeys } from './trace-graph-model'
import TraceGraphNode from './TraceGraphNode.vue'

const t = useLocalization('monitoring')

const props = defineProps<{
  roots: TraceNode[]
  /** A `spanKey`, which is unique. Selecting on `spanPath` would match every sibling sharing it. */
  selectedKey?: string
}>()

const emit = defineEmits<{
  (e: 'select', node: TraceNode): void
}>()

/**
 * Above this, wide subtrees start collapsed.
 *
 * Mounting is the cost that matters: each span is a component with its own reactive scope and half a
 * dozen elements, so a few thousand of them stall the tab before anything is painted. Collapsing by
 * default bounds the first paint and lets the operator open only the branch they care about.
 */
const LARGE_TRACE_SPANS = 500

/** `roots` is a list: a job whose ingress parent expired is a root in its own right. */
const hasSpans = computed(() => props.roots.length > 0)

/** Built once per trace and shared by every node, rather than each node measuring its own subtree. */
const model = computed(() => buildTraceGraphModel(props.roots))

const spanCount = computed(() => model.value.spans.length)

const isLarge = computed(() => spanCount.value > LARGE_TRACE_SPANS)

/** Overrides every collapsible node's own state; reset per trace so a new one opens collapsed. */
const expandAll = ref(false)

watch(
  () => props.roots,
  () => {
    expandAll.value = false
  },
)

/**
 * The selected span's ancestors, which are forced open regardless of the collapse default.
 *
 * Without this, a deep link can select a node that is not merely hard to spot but unrendered: a wide
 * subtree below `COLLAPSE_DEPTH` starts collapsed on a large trace, so a job scheduled by another job
 * lands inside a `+37` expander with nothing on screen to show it was found.
 *
 * Only the path to the selection opens. Expanding everything would give back the first-paint cost the
 * collapsing exists to avoid.
 */
const revealKeys = computed(() => findAncestorKeys(model.value, props.selectedKey))
</script>

<template>
  <div class="trace-graph">
    <p v-if="!hasSpans" class="empty">No spans available for this trace.</p>

    <template v-else>
      <!-- Only offered when it is needed: on a small trace nothing is collapsed, so the control
           would suggest hidden work that does not exist. -->
      <div v-if="isLarge" class="toolbar-row">
        <span class="hint">{{ t('trace.graph.collapsedHint', { count: spanCount }) }}</span>
        <button class="link-button btn-reset" type="button" @click="expandAll = !expandAll">
          {{ expandAll ? t('trace.graph.collapseAll') : t('trace.graph.expandAll') }}
        </button>
      </div>

      <ul class="roots" role="tree">
        <TraceGraphNode
          v-for="(root, index) in roots"
          :key="root.span.spanKey || `root-${index}`"
          :node="root"
          :depth="0"
          :model="model"
          :large-trace="isLarge"
          :expand-all="expandAll"
          :selected-key="selectedKey"
          :reveal-keys="revealKeys"
          @select="emit('select', $event)"
        />
      </ul>
    </template>
  </div>
</template>

<style scoped>
.trace-graph {
  overflow: auto;
  max-height: 50vh;
  padding: var(--space-2);
}

.empty {
  margin: 0;
  padding: 1rem;
  color: hsl(var(--color-40));
  font-size: 13px;
}

.roots {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 0;
  padding: 0;
  list-style: none;
  min-width: min-content;
}

.toolbar-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 8px;
}

.hint {
  font-size: 11px;
  color: hsl(var(--color-40));
}

.link-button {
  color: hsl(var(--color-30));
  cursor: pointer;
  font-size: 11px;
  text-decoration: underline;
}
</style>
